<template>
	<panel :show="show" class="filterPanel-section" @close="close">
		<template #title>
			Filter
		</template>
		<template>
			<div class="card">
				<div class="card-title multiline-title">
					<div class="card-title_left">
						<div class="card-title_left_name">
							Source
						</div>
						<div
							class="card-title_left_name-description"
							:class="{
								'card-title_left_name-description--error': isSourceSelectionEmpty,
							}"
						>
							At least one item must be selected
						</div>
					</div>
					<div class="card-title_right">
						<button @click="toggleCardDropdown('source')">
							<AccordionIconClose
								v-if="isSourceDropdownOpen"
								class="accordion-icon"
							/>
							<AccordionIconOpen v-else class="accordion-icon" />
						</button>
					</div>
				</div>

				<div v-if="isSourceDropdownOpen">
					<div class="card-list">
						<div class="dropdown-list">
							<div
								v-if="sourceOptions.length > 1"
								class="custom-checkbox select-checkbox-label"
							>
								<div class="select-all-label">
									<span
										class="cursor-pointer"
										@click="isAllSourcesSelected = true"
										>Select All</span
									>
									<span>|</span>
									<span
										class="cursor-pointer"
										@click="isAllSourcesSelected = false"
										>None</span
									>
								</div>
							</div>
							<!-- Source Section -->
							<div class="scrollable-list">
								<div
									v-for="(source,
									index) in sortedSourceOptions"
									:key="'source-' + index"
									class="custom-checkbox"
								>
									<input
										:id="'source-' + index"
										v-model="selectedSources"
										type="checkbox"
										:value="source"
									/>
									<label :for="'source-' + index">
										<span class="checkmark">
											<CheckIcon />
										</span>
										{{ source }}
									</label>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="card">
				<div class="card-title multiline-title">
					<div class="card-title_left">
						<div class="card-title_left_name">
							Call Types
						</div>
						<div
							class="card-title_left_name-description"
							:class="{
								'card-title_left_name-description--error': isCallTypesSelectionEmpty,
							}"
						>
							At least one item must be selected
						</div>
					</div>
					<div class="card-title_right">
						<button @click="toggleCardDropdown('callTypes')">
							<AccordionIconClose
								v-if="isCallTypesDropdownOpen"
								class="accordion-icon"
							/>
							<AccordionIconOpen v-else class="accordion-icon" />
						</button>
					</div>
				</div>

				<div v-if="isCallTypesDropdownOpen">
					<div class="card-list">
						<div class="dropdown-list">
							<div
								v-if="callTypeOptions.length > 1"
								class="custom-checkbox select-checkbox-label"
							>
								<div class="select-all-label">
									<span
										class="cursor-pointer"
										@click="isAllCallTypesSelected = true"
										>Select All</span
									>
									<span>|</span>
									<span
										class="cursor-pointer"
										@click="isAllCallTypesSelected = false"
										>None</span
									>
								</div>
							</div>
							<!-- Call Types Section -->
							<div class="scrollable-list">
								<div
									v-for="(type,
									index) in sortedCallTypeOptions"
									:key="'calltype-' + index"
									class="custom-checkbox"
								>
									<input
										:id="'calltype-' + index"
										v-model="selectedCallTypes"
										type="checkbox"
										:value="type"
									/>
									<label :for="'calltype-' + index">
										<span class="checkmark">
											<CheckIcon />
										</span>
										{{ type.type }}
									</label>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Office Hours Section -->
			<div v-if="officeOpenInfo.officeHoursSet" class="card">
				<div class="card-title multiline-title">
					<div class="card-title_left">
						<div class="card-title_left_name">
							Office Hours
						</div>
						<div
							class="card-title_left_name-description"
							:class="{
								'card-title_left_name-description--error':
									selectedOfficeHours.length === 0,
							}"
						>
							At least one item must be selected
						</div>
					</div>
					<div class="card-title_right">
						<button @click="toggleCardDropdown('officeHours')">
							<AccordionIconClose
								v-if="isOfficeHoursDropdownOpen"
								class="accordion-icon"
							/>
							<AccordionIconOpen v-else class="accordion-icon" />
						</button>
					</div>
				</div>

				<div v-if="isOfficeHoursDropdownOpen">
					<div class="card-list">
						<div class="dropdown-list">
							<div
								v-if="officeHoursOptions.length > 1"
								class="custom-checkbox select-checkbox-label"
							>
								<div class="select-all-label">
									<span
										class="cursor-pointer"
										@click="isAllOfficeHoursSelected = true"
										>Select All</span
									>
									<span>|</span>
									<span
										class="cursor-pointer"
										@click="
											isAllOfficeHoursSelected = false
										"
										>None</span
									>
								</div>
							</div>
							<!-- Office Hours Section -->
							<div class="scrollable-list">
								<div
									v-for="(type,
									index) in sortedOfficeHoursOptions"
									:key="'officehours-' + index"
									class="custom-checkbox"
								>
									<input
										:id="'officehours-' + index"
										v-model="selectedOfficeHours"
										type="checkbox"
										:value="type"
									/>
									<label
										:for="'officehours-' + index"
										class="text-capitalize"
									>
										<span class="checkmark">
											<CheckIcon />
										</span>
										{{ type }}
									</label>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="form-group form-button">
				<button
					class="action-button"
					:disabled="disableApplyButton"
					@click="applyFilter"
				>
					Apply
				</button>
			</div>
		</template>
	</panel>
</template>

<script>
import Panel from '@/components/Panels/Panel.vue'
import PanelMixin from '@/mixins/PanelMixin'
import AccordionIconClose from '@/components/Icons/AccordionIconClose.vue'
import AccordionIconOpen from '@/components/Icons/AccordionIconOpen.vue'
import { mapGetters, mapState } from 'vuex'
import {
	INSIGHT_CONFIG_LOCAL_STORAGE,
	NO_CALL_TYPE,
	OFFICE_HOURS_TYPES,
} from '@/config/constants'
import CheckIcon from '@/components/Icons/CheckIcon.vue'
export default {
	components: {
		CheckIcon,
		AccordionIconOpen,
		AccordionIconClose,
		Panel,
	},
	computed: {
		isAllSourcesSelected: {
			get() {
				return this.selectedSources.length === this.sourceOptions.length
			},
			set(value) {
				this.selectedSources = value ? [...this.sourceOptions] : []
			},
		},
		isAllCallTypesSelected: {
			get() {
				return (
					this.selectedCallTypes.length ===
					this.callTypeOptions.length
				)
			},
			set(value) {
				this.selectedCallTypes = value ? [...this.callTypeOptions] : []
			},
		},

		isAllOfficeHoursSelected: {
			get() {
				return (
					this.selectedOfficeHours.length ===
					this.officeHoursOptions.length
				)
			},
			set(value) {
				this.selectedOfficeHours = value
					? [...this.officeHoursOptions]
					: []
			},
		},

		officeHoursOptions() {
			return this.officeOpenInfo.lunchHoursSet
				? OFFICE_HOURS_TYPES
				: OFFICE_HOURS_TYPES.filter(t => t !== 'lunch')
		},

		disableApplyButton() {
			const sourcesEmpty = (this.selectedSources || []).length === 0
			const hasCallTypeOptions = (this.callTypeOptions || []).length > 0
			const officeHoursTypesEmpty =
				this.officeOpenInfo.officeHoursSet &&
				(this.selectedOfficeHours || []).length === 0

			// If there are no call type options available, only check sources
			if (!hasCallTypeOptions) {
				return sourcesEmpty
			}

			// If call type options exist, check both sources and call types
			const callTypesEmpty = (this.selectedCallTypes || []).length === 0
			return sourcesEmpty || callTypesEmpty || officeHoursTypesEmpty
		},

		isSourceSelectionEmpty() {
			return (this.selectedSources || []).length === 0
		},

		isCallTypesSelectionEmpty() {
			const hasCallTypeOptions = (this.callTypeOptions || []).length > 0
			if (!hasCallTypeOptions) return false
			return (this.selectedCallTypes || []).length === 0
		},

		...mapGetters({
			partner: 'partners/active',
			internalCallTypes: 'callTypes/get',
			hasPagingEnabled: 'partners/hasPagingEnabled',
		}),
		...mapState('settings', {
			officeOpenInfo: state => state.officeOpenInfo,
		}),
	},

	methods: {
		getSortedSourceOptions() {
			const selected = this.sourceOptions
				.filter(item => this.selectedSources.includes(item))
				.sort()
			const unselected = this.sourceOptions
				.filter(item => !this.selectedSources.includes(item))
				.sort()
			this.sortedSourceOptions = [...selected, ...unselected]
		},

		getSortedCallTypeOptions() {
			const noCallType = []
			const selected = []
			const unselected = []

			this.callTypeOptions.forEach(item => {
				if (item.type === NO_CALL_TYPE) {
					noCallType.push(item)
				} else if (this.selectedCallTypes.includes(item.index)) {
					selected.push(item)
				} else {
					unselected.push(item)
				}
			})

			this.sortedCallTypeOptions = [
				...this.sortByType(selected),
				...this.sortByType(unselected),
				...noCallType,
			]
		},

		getSortedOfficeHoursOptions() {
			this.sortedOfficeHoursOptions = this.getSortedOnce(
				this.officeHoursOptions,
				this.selectedOfficeHours
			)
		},

		sortByType(list) {
			return list.sort((a, b) => {
				const aVal = typeof a === 'object' && a !== null ? a.type : a
				const bVal = typeof b === 'object' && b !== null ? b.type : b
				return String(aVal)
					.toLowerCase()
					.localeCompare(String(bVal).toLowerCase())
			})
		},

		toggleCardDropdown(type) {
			if (type === 'source') {
				this.isSourceDropdownOpen = !this.isSourceDropdownOpen
			} else if (type === 'callTypes') {
				this.isCallTypesDropdownOpen = !this.isCallTypesDropdownOpen
			} else if (type === 'officeHours') {
				this.isOfficeHoursDropdownOpen = !this.isOfficeHoursDropdownOpen
			}
		},

		toggleSelectAll(type) {
			if (type === 'source') {
				this.selectedSources = this.isAllSourcesSelected
					? []
					: [...this.sourceOptions]
			} else if (type === 'callTypes') {
				this.selectedCallTypes = this.isAllCallTypesSelected
					? []
					: [...this.callTypeOptions]
			} else if (type === 'officeHours') {
				this.selectedOfficeHours = this.isAllOfficeHoursSelected
					? []
					: [...this.officeHoursOptions]
			}
		},

		getSortedOnce(allOptions, selectedList) {
			const selected = []
			const unselected = []
			let noCallTypeOption = null

			const isNoCallType = opt =>
				typeof opt === 'object' && opt.type === NO_CALL_TYPE

			allOptions.forEach(opt => {
				if (isNoCallType(opt)) {
					noCallTypeOption = opt
				} else {
					const isSelected =
						typeof opt === 'object'
							? selectedList.some(item => item.type === opt.type)
							: selectedList.includes(opt)

					if (isSelected) {
						selected.push(opt)
					} else {
						unselected.push(opt)
					}
				}
			})

			const result = [
				...this.sortByType(selected),
				...this.sortByType(unselected),
			]

			if (noCallTypeOption) result.push(noCallTypeOption)
			return result
		},

		applyFilter() {
			const filtered = {
				sourceTypes: this.selectedSources.map(a => {
					const lower = a.toLowerCase()
					return lower === 'web message'
						? 'wma'
						: lower.substring(0, a.length - 1)
				}),
				callTypes: this.isAllCallTypesSelected
					? []
					: this.selectedCallTypes.map(item => item.index),
				officeHours: this.officeOpenInfo.officeHoursSet
					? this.selectedOfficeHours.map(item => item.toLowerCase())
					: [],
			}
			const savedInsightConfig = JSON.parse(
				localStorage.getItem(INSIGHT_CONFIG_LOCAL_STORAGE)
			)
			const officeHoursSelection = this.officeOpenInfo.officeHoursSet
				? this.selectedOfficeHours
				: []
			const updatedConfig = {
				...savedInsightConfig,
				sourceTypes: this.selectedSources.map(a => {
					const lower = a.toLowerCase()
					return lower === 'web message'
						? 'wma'
						: lower.substring(0, a.length - 1)
				}),
				callTypes: this.selectedCallTypes.map(item => item.index),
				officeHours: officeHoursSelection,
				filterSelectedSources: this.selectedSources,
				filterSelectedCallTypes: this.selectedCallTypes,
				filterSelectedOfficeHours: officeHoursSelection,
			}
			localStorage.setItem(
				INSIGHT_CONFIG_LOCAL_STORAGE,
				JSON.stringify(updatedConfig)
			)
			this.$emit('submit', filtered)
			this.close()
		},

		resetOrPopulateForm() {
			const savedInsightConfig = JSON.parse(
				localStorage.getItem(INSIGHT_CONFIG_LOCAL_STORAGE)
			)
			this.selectedSources = savedInsightConfig?.filterSelectedSources || [
				'Calls',
			]
			this.selectedCallTypes =
				savedInsightConfig?.filterSelectedCallTypes ||
				this.callTypeOptions

			if (this.officeOpenInfo.officeHoursSet) {
				const o = this.officeHoursOptions
				const raw = savedInsightConfig?.filterSelectedOfficeHours
				const pick = Array.isArray(raw)
					? raw.filter(x => o.includes(x))
					: []
				this.selectedOfficeHours = pick.length ? pick : [...o]
			} else {
				this.selectedOfficeHours = []
			}
		},

		ensureNoCallTypeOption() {
			const hasNoCallType = this.callTypeOptions.some(
				option => option.type === NO_CALL_TYPE
			)

			if (!hasNoCallType) {
				this.callTypeOptions.push({
					index: 0,
					type: NO_CALL_TYPE,
				})
			}
		},
	},

	mixins: [PanelMixin],

	name: 'FilterPanel',

	watch: {
		show(val) {
			if (val) {
				this.resetOrPopulateForm()

				this.ensureNoCallTypeOption()

				this.sortedSourceOptions = this.getSortedOnce(
					this.sourceOptions,
					this.selectedSources
				)
				this.sortedCallTypeOptions = this.getSortedOnce(
					this.callTypeOptions,
					this.selectedCallTypes
				)
				if (this.officeOpenInfo.officeHoursSet) {
					this.sortedOfficeHoursOptions = this.getSortedOnce(
						this.officeHoursOptions,
						this.selectedOfficeHours
					)
				} else {
					this.sortedOfficeHoursOptions = []
				}
			}
		},
		'partner.id': {
			handler(newPartnerId, oldPartnerId) {
				if (newPartnerId !== oldPartnerId) {
					this.sourceOptions = [
						'Calls',
						...(this.hasPagingEnabled ? ['Pages'] : []),
						...(this.partner?.wma_submit &&
						this.officeOpenInfo.visible
							? ['Web Message']
							: []),
					]
					this.$store.dispatch('callTypes/get')
					this.selectedSources = ['Calls']
					this.selectedCallTypes = []
					this.selectedOfficeHours = []
				}
			},
			immediate: true,
		},
		internalCallTypes: {
			handler(newTypes) {
				if (Array.isArray(newTypes)) {
					this.callTypeOptions = newTypes.map(type => {
						return {
							index: type.id,
							type: type.name,
						}
					})
					this.ensureNoCallTypeOption()
				}
			},
			immediate: true,
		},
	},

	created() {
		this.resetOrPopulateForm()
	},

	mounted() {
		this.getSortedSourceOptions()
		this.getSortedCallTypeOptions()
		if (this.officeOpenInfo.officeHoursSet) {
			this.getSortedOfficeHoursOptions()
		}
	},

	data() {
		return {
			isSourceDropdownOpen: true,
			isCallTypesDropdownOpen: true,
			sourceOptions: ['Calls', 'Pages', 'Web Message'],
			isOfficeHoursDropdownOpen: true,
			callTypeOptions: [],
			selectedSources: ['Calls'],
			selectedCallTypes: [],
			selectedOfficeHours: [],
			sortedSourceOptions: [],
			sortedCallTypeOptions: [],
			sortedOfficeHoursOptions: [],
		}
	},
}
</script>
